import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { BirBirDeliveryBanner } from 'atomic/organisms/offer/OfferLocation/BirBirDeliveryBanner';

import { useInViewOnce } from 'hooks/useInViewOnce';
import useMedia from 'hooks/useMedia';
import { getTestIdByParts } from 'utils/getTestIdByParts';
import type { WithTestId } from 'types/app';

import { ModalNamesEnum } from '@Modules/app/constants';
import { openModal } from '@Modules/app/slice';
import type { Resolutions } from '@Modules/device/types';
import type { LocationType } from '@Modules/geo/types';
import type { DeliveryType } from '@Modules/offers/types';

import { CourierDeliveryBanner } from '@Atomic/organisms/offer/OfferLocation/CourierDeliveryBanner';
import { PointMap } from '@Atomic/molecules/PointMap';
import { Button } from '@Atomic/atoms/Button';
import { Icons } from '@Atomic/atoms/Icons';
import { IconWrapper } from '@Atomic/atoms/IconWrapper';
import { Text } from '@Atomic/atoms/Text';

import { DeliveryAvailableModal } from './DeliveryAvailableModal';
import styles from './style.module.scss';

type Props = {
  className?: string;
  point: LocationType;
  fullAddress: string;
  title: string;
  delivery?: DeliveryType;
  courierDelivery: boolean;
  description: string | null;
};

type SIZE_MAPPING_TYPE = Record<'xs' | 'sm' | 'md' | 'lg', number>;

const MAP_HEIGHT_MAPPING: SIZE_MAPPING_TYPE = {
  xs: 170,
  sm: 244,
  md: 220,
  lg: 220,
};

const getMapHeight = (resolutions: Resolutions) => {
  if (resolutions.lg) {
    return MAP_HEIGHT_MAPPING.lg;
  }

  if (resolutions.md) {
    return MAP_HEIGHT_MAPPING.md;
  }

  if (resolutions.sm) {
    return MAP_HEIGHT_MAPPING.sm;
  }

  return MAP_HEIGHT_MAPPING.xs;
};

export const OfferLocation = ({
  className,
  point,
  title,
  testId,
  delivery,
  description,
  fullAddress,
  courierDelivery,
}: WithTestId<Props>) => {
  const { t } = useTranslation();
  const resolutions = useMedia();
  const dispatch = useDispatch();
  const [isOpen, setOpen] = useState(false);
  const mapHeight = getMapHeight(resolutions);
  // The Yandex Maps API is ~700 KB of script: load it only when the map is about to be seen
  const [mapSlotRef, isMapSlotNear] = useInViewOnce<HTMLDivElement>();

  const handleBirBirDeliveryModalOpen = (): void => {
    dispatch(
      openModal({
        name: ModalNamesEnum.DOWNLOAD_APP,
        payload: {
          source: 'delivery',
        },
      })
    );
  };

  const handleCourierDeliveryModalOpen = (): void => {
    setOpen(true);
  };

  let DeliveryBanner = null;

  if (courierDelivery) {
    DeliveryBanner = (
      <CourierDeliveryBanner testId={testId} onClick={handleCourierDeliveryModalOpen} />
    );
  }

  if (delivery) {
    DeliveryBanner = (
      <BirBirDeliveryBanner
        label={delivery.label}
        badge={delivery.buttonBadge}
        testId={testId}
        onClick={handleBirBirDeliveryModalOpen}
      />
    );
  }

  return (
    <div className={className} data-testid={testId}>
      <div className={styles.deliveryWrapper}>
        <div
          ref={mapSlotRef}
          className={styles.deliveryMap}
          data-testid={getTestIdByParts(testId, 'map')}
        >
          {isMapSlotNear ? (
            <PointMap height={mapHeight} value={point} />
          ) : (
            <div className={styles.mapPlaceholder} style={{ height: mapHeight }} />
          )}
        </div>
        {DeliveryBanner}
      </div>

      {delivery && (
        <Button
          className={styles.deliveryButton}
          intent="accent"
          presentation="tonal"
          fullWidth
          onClick={handleBirBirDeliveryModalOpen}
          testId={getTestIdByParts(testId, 'delivery-button')}
        >
          {/* server-localized, no t() needed */}
          {delivery.button}
        </Button>
      )}

      <div className={styles.deliveryDescription} data-testid={getTestIdByParts(testId, 'address')}>
        <IconWrapper className={styles.descriptionIcon}>
          <Icons.Search color="var(--fg-tertiary)" width={20} height={20} />
        </IconWrapper>

        <div className={styles.deliveryText}>
          <Text>{fullAddress}</Text>
          <Text className={styles.deliveryTitle} variant="medium">
            {title}
          </Text>
        </div>
      </div>

      {description && (
        <div
          className={styles.deliveryDescription}
          data-testid={getTestIdByParts(testId, 'description')}
        >
          <IconWrapper className={styles.descriptionIcon}>
            <Icons.LandmarkFill color="var(--fg-tertiary)" width={20} height={20} />
          </IconWrapper>

          <div className={styles.deliveryText}>
            <Text>{description}</Text>
            <Text className={styles.deliveryTitle} variant="medium">
              {t('offer.landmark')}
            </Text>
          </div>
        </div>
      )}

      <DeliveryAvailableModal visible={isOpen} onClose={() => setOpen(false)} />
    </div>
  );
};
